import React, { useEffect } from 'react';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  IndentIncrease,
  IndentDecrease,
} from 'lucide-react';

/**
 * TipTap-based rich text editor used inside the EditableText edit dialog.
 * Supports: Bold, Italic, Underline, Bullet list, Numbered list, and Indent /
 * Outdent everywhere (Tab / Shift+Tab). In a list, indent nests the list item;
 * in a paragraph, indent adds left padding via a `data-indent` attribute.
 *
 * Value is HTML. Setting `value` from outside re-syncs the editor content.
 */

// Indent extension: adds a `data-indent` attribute (0..MAX) to paragraphs and
// headings only — list items handle their own indentation via sink/lift so
// data-indent must NEVER land on list-item children (avoids double indent).
const MAX_INDENT = 8;
const INDENTABLE_TYPES = ['paragraph', 'heading'];

const Indent = Extension.create({
  name: 'indent',

  addGlobalAttributes() {
    return [
      {
        types: INDENTABLE_TYPES,
        attributes: {
          indent: {
            default: 0,
            parseHTML: (el) => {
              const val = parseInt(el.getAttribute('data-indent') || '0', 10);
              return Number.isFinite(val) && val > 0 ? val : 0;
            },
            renderHTML: (attrs) => {
              if (!attrs.indent) return {};
              return { 'data-indent': String(attrs.indent) };
            },
            // Don't carry the attribute across Enter splits — a fresh
            // paragraph starts back at indent 0.
            keepOnSplit: false,
          },
        },
      },
    ];
  },

  addCommands() {
    // Bump / drop the indent attribute on the SINGLE node that contains the
    // current selection. Uses $from.before() so we never accidentally walk
    // the whole document (which was causing the wrong paragraph to indent
    // when the caret was inside a list item).
    const step = (delta) => ({ state, dispatch }) => {
      const { $from } = state.selection;
      const node = $from.node();
      if (!INDENTABLE_TYPES.includes(node.type.name)) return false;
      // Never touch paragraphs / headings that live inside a list — the list
      // itself handles their indentation.
      const container = $from.node(-1);
      if (container && container.type.name === 'listItem') return false;
      const current = node.attrs.indent || 0;
      const next = Math.min(MAX_INDENT, Math.max(0, current + delta));
      if (next === current) return false;
      if (dispatch) {
        dispatch(state.tr.setNodeMarkup($from.before(), undefined, { ...node.attrs, indent: next }));
      }
      return true;
    };
    return {
      indent: () => step(1),
      outdent: () => step(-1),
    };
  },

  addKeyboardShortcuts() {
    // Always return true so ProseMirror swallows Tab / Shift-Tab and focus
    // can't escape the editor. Prefer list-item sink/lift when available; if
    // that command reports it didn't apply (not in a list, or first item),
    // fall back to paragraph indent.
    return {
      Tab: () => {
        const { editor } = this;
        if (editor.commands.sinkListItem('listItem')) return true;
        editor.commands.indent();
        return true;
      },
      'Shift-Tab': () => {
        const { editor } = this;
        if (editor.commands.liftListItem('listItem')) return true;
        editor.commands.outdent();
        return true;
      },
    };
  },

  // Whenever the document changes (e.g. after wrapping a paragraph into a
  // list), reset the `indent` attribute on any indentable node that lives
  // inside a listItem. Prevents double indentation (list padding + data-indent
  // padding) and orphan `data-indent="1"` attributes that couldn't be cleared
  // while the cursor was inside the list.
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('indent-strip-in-list'),
        appendTransaction: (_transactions, _oldState, newState) => {
          let tr = newState.tr;
          let changed = false;
          newState.doc.descendants((node, pos, parent) => {
            if (!INDENTABLE_TYPES.includes(node.type.name)) return true;
            if (!parent || parent.type.name !== 'listItem') return false;
            if (!node.attrs.indent) return false;
            tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: 0 });
            changed = true;
            return false;
          });
          return changed ? tr : null;
        },
      }),
    ];
  },
});

const ToolbarButton = ({ onClick, active, disabled, title, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    data-testid={testid}
    className={`inline-flex items-center justify-center w-8 h-8 border text-sm ${
      active
        ? 'bg-[#3a2359] text-white border-[#3a2359]'
        : 'bg-white text-[#3a2359] border-[#e9e0cc] hover:border-[#3a2359]'
    } disabled:opacity-40 disabled:cursor-not-allowed`}
  >
    {children}
  </button>
);

const RichTextEditor = ({ value = '', onChange, minRows = 8 }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // StarterKit v3 bundles paragraph, bold, italic, underline, link, lists,
        // history, etc. We disable the block types this parish site does not use.
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        link: {
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
          HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
        },
      }),
      Indent,
    ],
    content: value || '<p></p>',
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      // TipTap represents empty content as "<p></p>" — normalize to empty string.
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  // Re-sync when the parent updates the value (e.g. dialog reopens with new content).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || '<p></p>';
    if (current !== next) editor.commands.setContent(next, { emitUpdate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const doIndent = () => {
    // Same fallback pattern as the Tab shortcut — try list nesting first,
    // otherwise fall back to paragraph indent. `sinkListItem` self-reports
    // whether it applied so we don't rely on possibly-stale isActive checks.
    if (editor.chain().focus().sinkListItem('listItem').run()) return;
    editor.chain().focus().indent().run();
  };
  const doOutdent = () => {
    if (editor.chain().focus().liftListItem('listItem').run()) return;
    editor.chain().focus().outdent().run();
  };

  return (
    <div className="border border-[#e9e0cc] bg-white focus-within:border-[#3a2359]" data-testid="rich-text-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 border-b border-[#e9e0cc] px-2 py-2 bg-[#faf7f0]">
        <ToolbarButton
          testid="rt-bold"
          title="Bold (Ctrl/Cmd+B)"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <BoldIcon size={14} />
        </ToolbarButton>
        <ToolbarButton
          testid="rt-italic"
          title="Italic (Ctrl/Cmd+I)"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <ItalicIcon size={14} />
        </ToolbarButton>
        <ToolbarButton
          testid="rt-underline"
          title="Underline (Ctrl/Cmd+U)"
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon size={14} />
        </ToolbarButton>
        <span className="w-px h-6 bg-[#e9e0cc] mx-1" />
        <ToolbarButton
          testid="rt-bullet-list"
          title="Bullet list"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List size={14} />
        </ToolbarButton>
        <ToolbarButton
          testid="rt-ordered-list"
          title="Numbered list"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered size={14} />
        </ToolbarButton>
        <span className="w-px h-6 bg-[#e9e0cc] mx-1" />
        <ToolbarButton
          testid="rt-outdent"
          title="Decrease indent (Shift+Tab)"
          onClick={doOutdent}
        >
          <IndentDecrease size={14} />
        </ToolbarButton>
        <ToolbarButton
          testid="rt-indent"
          title="Increase indent (Tab)"
          onClick={doIndent}
        >
          <IndentIncrease size={14} />
        </ToolbarButton>
      </div>

      {/* Editor surface */}
      <EditorContent
        editor={editor}
        className="rt-editor px-4 py-3 text-sm leading-relaxed"
        style={{ minHeight: `${minRows * 1.75}rem` }}
      />
    </div>
  );
};

export default RichTextEditor;
